import { RecurrenceFrequency, RecurrenceValues } from './types';

const ALL_FIELDS = [
  'weekday',
  'month',
  'setpos',
  'week_number',
  'month_day',
  'year_day',
] as const;

export type RecurrenceField = typeof ALL_FIELDS[number];

// RFC 5545 3.3.10 — rrule.js enforces none of it: unchecked, DAILY + BYYEARDAY renders as
// "every day" while firing once a year
const ALLOWED_BY_FREQ: Record<RecurrenceFrequency, readonly RecurrenceField[]> = {
  daily: ['weekday', 'month', 'month_day', 'setpos'],
  weekly: ['weekday', 'month', 'setpos'],
  monthly: ['weekday', 'month', 'month_day', 'setpos'],
  yearly: ALL_FIELDS,
};

// BYHOUR left out: setpos over hours yields nothing useful
const SETPOS_COMPANIONS: readonly RecurrenceField[] = [
  'weekday',
  'month',
  'week_number',
  'month_day',
  'year_day',
];

// 0 counts as unset: no BY-part accepts it, and buildRecurrenceRule drops it too
const hasValue = (values: RecurrenceValues, field: RecurrenceField): boolean => {
  const value = values?.[field];
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

export const allowedFields = (values: RecurrenceValues): readonly RecurrenceField[] => {
  if (!values?.freq) return ALL_FIELDS;
  const allowed = ALLOWED_BY_FREQ[values.freq];
  if (!allowed.includes('setpos')) return allowed;
  // RFC 5545: BYSETPOS must only be used in conjunction with another BY-part, and only one this
  // frequency keeps — a companion that is itself about to be dropped cannot justify it
  const companion = SETPOS_COMPANIONS.some(
    (field) => allowed.includes(field) && hasValue(values, field)
  );
  return companion ? allowed : allowed.filter((field) => field !== 'setpos');
};

// stricter than the RFC: Daily has always meant "no refinement" in both dialogs
export const isFieldDisabled = (values: RecurrenceValues, field: RecurrenceField): boolean =>
  values?.freq === 'daily' || !allowedFields(values).includes(field);

export const clearedFields = (values: RecurrenceValues): RecurrenceValues =>
  ALL_FIELDS.filter((field) => isFieldDisabled(values, field))
    .filter((field) => hasValue(values, field))
    .reduce<RecurrenceValues>((patch, field) => ({ ...patch, [field]: undefined }), {});

// drops RFC-invalid parts only, never merely UI-disabled ones: the back end applies stored
// fields as they are, so dropping more would make the preview disagree with the scheduler
export const sanitizeRecurrence = (values: RecurrenceValues): RecurrenceValues => {
  if (!values?.freq) return values;
  const allowed = allowedFields(values);
  const dropped = ALL_FIELDS.filter((field) => !allowed.includes(field));
  if (!dropped.length) return values;
  return dropped.reduce<RecurrenceValues>(
    (result, field) => ({ ...result, [field]: undefined }),
    values
  );
};
